import { CASE_REFERENCE_ANCHOR_PREFIX } from '@/domain/industrialCase/industrialCaseCitation';
import type { IndustrialCaseDocument } from '@/domain/industrialCase/industrialCaseDocument';

import { IconArrowUpRightFromSquareRound } from '@/components/apps/ui/icon';

type Reference = IndustrialCaseDocument['references'][number];

/**
 * ### IndustrialCaseDocumentReferences
 *
 * The reference list closing a case.
 *
 * Anchors match the ids that citation markers link to, so the `[n]` in the body
 * jumps to the right entry, and numbering comes from the array order rather
 * than from the document — reordering references never leaves a stale number.
 *
 * @param props The references, the section title, and the external-link label
 * @returns The numbered reference list
 */
export function IndustrialCaseDocumentReferences({
  externalLinkLabel,
  references,
  title,
}: {
  externalLinkLabel: string;
  references: ReadonlyArray<Reference>;
  title: string;
}) {
  return (
    <section className="max-w-[720px]">
      <h2 className="text-[clamp(28px,3.2vw,38px)] font-semibold leading-[1.2] tracking-[-0.025em] text-apps-brandGrayBlack">
        {title}
      </h2>

      <ol className="mt-8 grid list-none gap-8 border-t border-[#d8d8d4] pt-8 md:gap-9">
        {references.map((reference, index) => (
          <li
            className="grid scroll-mt-28 grid-cols-[36px_minmax(0,1fr)] gap-3 md:grid-cols-[44px_minmax(0,1fr)] md:gap-4"
            id={`${CASE_REFERENCE_ANCHOR_PREFIX}-${index + 1}`}
            key={reference.id}
          >
            <span className="pt-0.5 text-[13px] font-semibold leading-[1.6] text-[#787873] tabular-nums">
              [{index + 1}]
            </span>
            <div className="min-w-0">
              {reference.url ? (
                <a
                  className="group/reference flex w-full items-center gap-2 text-[#242421] transition-colors duration-150 ease-out hover:text-[#ed1745] motion-reduce:transition-none"
                  href={reference.url}
                  target="_blank"
                  rel="noreferrer"
                  aria-label={`${reference.title} ${externalLinkLabel}`}
                >
                  <cite className="min-w-0 text-lg font-semibold leading-[1.55] not-italic">
                    {reference.title}
                  </cite>
                  <span className="grid shrink-0 place-items-center text-[#777772] transition-colors duration-150 ease-out group-hover/reference:text-[#ed1745] motion-reduce:transition-none">
                    <IconArrowUpRightFromSquareRound
                      size={20}
                      aria-hidden="true"
                    />
                  </span>
                </a>
              ) : (
                <cite className="block text-lg font-semibold leading-[1.55] text-[#242421] not-italic">
                  {reference.title}
                </cite>
              )}
              <p className="text-sm font-medium leading-[1.6] text-[#74746f]">
                {reference.source}
                {reference.fileName ? ` · ${reference.fileName}` : ''}
              </p>
            </div>
          </li>
        ))}
      </ol>
    </section>
  );
}
